// ---------------------------------------------------------------------------
// Table browser — shown at "/" instead of auto-creating a room. Pick a game
// type to start a fresh table, rejoin a live/saved one, or enter a code.
// ---------------------------------------------------------------------------

import { shortId } from "../shared/math";
import { getTableSpec, TABLE_TYPES } from "../shared/tables";

interface RoomEntry {
  id: string;
  type: string;
  players: string[];
  objects: number;
  at: number;
  live: boolean;
}

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function showLobby(root: HTMLElement): void {
  const wrap = document.createElement("div");
  wrap.className = "lobby";
  const cards = TABLE_TYPES.map(
    (t) => `
    <button class="lobby-card" data-type="${t.id}" style="--accent:${t.accent}">
      <span class="lobby-icon">${t.icon}</span>
      <span class="lobby-card-head">
        <span class="lobby-card-name">${esc(t.name)}</span>
        <span class="lobby-card-players">${esc(t.players)} players</span>
      </span>
      <span class="lobby-card-tag">${esc(t.tagline)}</span>
    </button>`,
  ).join("");

  wrap.innerHTML = `
    <div class="lobby-inner">
      <h1>TABLETOP <span>online</span></h1>
      <p class="lobby-sub">A shared table. Decks, chips, hands — with friends, in the browser.</p>
      <h2>Start a new table</h2>
      <div class="lobby-grid">${cards}</div>
      <h2>Join a table</h2>
      <div class="lobby-join">
        <input type="text" maxlength="24" placeholder="table code (e.g. k3v9x2)" spellcheck="false" />
        <button class="lobby-go">Join</button>
      </div>
      <div class="lobby-rooms"><div class="lobby-loading">Looking for tables…</div></div>
    </div>`;
  root.append(wrap);

  wrap.querySelectorAll<HTMLElement>(".lobby-card").forEach((b) => {
    b.onclick = () => {
      const type = b.dataset.type!;
      location.href = `/${shortId()}?new=${encodeURIComponent(type)}`;
    };
  });

  const input = wrap.querySelector<HTMLInputElement>(".lobby-join input")!;
  const join = () => {
    const id = input.value.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (id) location.href = `/${id}`;
  };
  wrap.querySelector<HTMLElement>(".lobby-go")!.onclick = join;
  input.onkeydown = (e) => {
    if (e.key === "Enter") join();
  };

  void loadRooms(wrap.querySelector<HTMLElement>(".lobby-rooms")!);
}

async function loadRooms(el: HTMLElement): Promise<void> {
  let entries: RoomEntry[] = [];
  try {
    const res = await fetch("/api/rooms");
    entries = ((await res.json()) as { rooms: RoomEntry[] }).rooms ?? [];
  } catch {
    el.innerHTML = `<div class="lobby-loading">Couldn't reach the server.</div>`;
    return;
  }
  if (entries.length === 0) {
    el.innerHTML = `<div class="lobby-loading">No tables yet — start one above.</div>`;
    return;
  }
  el.innerHTML = entries
    .slice(0, 30)
    .map((r) => {
      const spec = getTableSpec(r.type);
      const status = r.live
        ? `<span class="lobby-live">● ${r.players.length ? esc(r.players.join(", ")) : "open"}</span>`
        : `<span class="lobby-saved">saved · ${r.objects} objects</span>`;
      return `<button class="lobby-room" data-room="${esc(r.id)}">
        <span class="lobby-room-code">⌗ ${esc(r.id)}</span>
        <span class="lobby-room-type" style="color:${spec.accent}">${spec.icon} ${esc(spec.name)}</span>
        ${status}
      </button>`;
    })
    .join("");
  el.querySelectorAll<HTMLElement>(".lobby-room").forEach((b) => {
    b.onclick = () => {
      location.href = `/${b.dataset.room}`;
    };
  });
}
